import { MessageCircleIcon } from "lucide-react";
import { useParams } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { Card } from "@/components/card";
import { LoadingScreen } from "@/components/loading-screen";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { RoleEnum } from "@classview/shared-types";

import { useFetchRecordingComments } from "../api/fetch-recording-comments";

import { AddComment } from "./add-comment";
import { CommentItem } from "./comment-item";

type CommentsContainerProps = {
  videoRef: React.RefObject<HTMLVideoElement>;
};

export function CommentsContainer(props: CommentsContainerProps) {
  const { videoRef } = props;
  const { id: recordingId } = useParams();

  const { organisation, hasPermission } = useOrganisation();

  const organisationHasCommentPermission = hasPermission([
    RoleEnum.REPLAY,
    RoleEnum.REPLAY_ADVANCED,
  ]);

  const {
    data: comments,
    isLoading,
    error,
    isError,
    isSuccess,
  } = useFetchRecordingComments(
    {
      organisationId: organisation.id,
      recordingId: recordingId || "",
    },
    {
      select: (comments) => {
        return comments.filter((comment) => comment.parentCommentId === null);
      },
    },
  );

  const jumpToTimestamp = (timestamp: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = timestamp;
    }
  };

  if (!organisation.replayCommentEnabled) {
    return;
  }

  if (isLoading) {
    return (
      <Card>
        <Card.Header title="Comments" />
        <Card.Content>
          <LoadingScreen />
        </Card.Content>
      </Card>
    );
  }

  if (isError || !isSuccess) {
    return (
      <Card>
        <Card.Header title="Comments" />
        <Card.Content>
          <ErrorMessage error={error} />
        </Card.Content>
      </Card>
    );
  }

  if (!organisationHasCommentPermission && comments.length === 0) {
    return;
  }

  return (
    <Card>
      <Card.Header title="Comments" />
      {organisation.canComment && organisationHasCommentPermission && (
        <Card.Content className="p-0">
          <AddComment />
        </Card.Content>
      )}
      <Card.Content className="space-y-4">
        {comments.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            handleJumpToTimestamp={jumpToTimestamp}
          />
        ))}

        {comments.length === 0 && (
          <div className="flex flex-col items-center justify-center p-4">
            <MessageCircleIcon className="mb-2 h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No comments yet. Be the first to comment!
            </p>
          </div>
        )}
      </Card.Content>
    </Card>
  );
}
